/**
 * Mock 端点实现 —— 在后端就绪前驱动 UI。
 *
 * 刻意做到的三件事（否则 mock 会把 UI 惯坏，联调时集体崩塌）：
 * 1. **有延迟**：默认 320ms，逼着每个页面把 Loading 态真正实现出来。
 * 2. **会失败**：`?__mock=error` 触发错误路径，让 Error 态可被人工验收。
 * 3. **真分页真筛选**：分页/筛选逻辑在这里跑一遍，避免前端写出「假装筛过了」的表格。
 */
import type {
  Activation,
  AdminUser,
  ApiKey,
  CodeBatch,
  Customer,
  CustomerInput,
  CreateOrderRequest,
  License,
  LicenseCode,
  LicenseDetail,
  ListLicensesQuery,
  Order,
  Paged,
  Plan,
  Product,
  ProductIntegration,
  ReadinessData,
  RedemptionRequest,
  RevokeResult,
  Session,
  StatsOverview,
  EntitlementKey,
  CreateEntitlementKeyRequest,
  UpdateEntitlementKeyRequest,
  CreateLicenseRequest,
  PlanSnapshot,
  EntitlementValue,
  CreateApiKeyResult,
  IpBlacklistEntry,
} from '../api/types';
import type { AuditEvent, CompromiseKeyResult, RotateKeyResult, SigningKey } from '../api/endpoints';
import type { ProductVersion } from '../api/types';
import { ApiError } from '../api/client';
import {
  MOCK_ADMIN_USERS,
  MOCK_API_KEYS,
  MOCK_ACTIVATIONS,
  MOCK_AUDIT_EVENTS,
  MOCK_CODE_BATCHES,
  MOCK_CUSTOMERS,
  MOCK_INVENTORY,
  MOCK_LICENSE_CODES,
  MOCK_LICENSES,
  MOCK_ORDERS,
  MOCK_PLANS,
  MOCK_PRODUCTS,
  MOCK_PRODUCT_VERSIONS,
  MOCK_SESSIONS,
  MOCK_SIGNING_KEYS,
  MOCK_STATS,
  NOW,
  codeMask,
  mockPlainFromMask,
} from './data';
import type { InventoryRow } from './data';

const LATENCY_MS = Number.parseInt(import.meta.env.VITE_MOCK_LATENCY_MS ?? '320', 10);

function delay<T>(value: T): Promise<T> {
  const ms = Number.isFinite(LATENCY_MS) ? LATENCY_MS : 320;
  return new Promise((resolve) => {
    globalThis.setTimeout(() => resolve(value), ms);
  });
}

/** 允许在地址栏用 ?__mock=error / ?__mock=empty 手动验收 5 态，不需要改代码 */
type MockMode = 'normal' | 'error' | 'empty';

function currentMode(): MockMode {
  const value = new URLSearchParams(globalThis.location.search).get('__mock');
  return value === 'error' || value === 'empty' ? value : 'normal';
}

function guard(action: string): void {
  if (currentMode() === 'error') {
    throw new ApiError({
      code: -1,
      message: `请求超时（30s）`,
      httpStatus: 0,
      transport: true,
    });
  }
  void action;
}

function paginate<T>(rows: T[], page: number, limit: number): Paged<T> {
  const start = (page - 1) * limit;
  const items = rows.slice(start, start + limit);
  return { items, total: rows.length, page, limit, hasMore: start + items.length < rows.length };
}

export async function mockStatsOverview(): Promise<StatsOverview> {
  guard('加载运行概览');
  return delay(MOCK_STATS);
}

export async function mockReadiness(): Promise<ReadinessData> {
  guard('检查服务就绪状态');
  return delay({ api: 'up', database: 'up', signer: 'up', queue: 'not_configured', backup: 'not_configured' });
}

export async function mockInventory(): Promise<InventoryRow[]> {
  guard('加载库存水位');
  return delay(currentMode() === 'empty' ? [] : MOCK_INVENTORY);
}

export async function mockProducts(): Promise<Paged<Product>> {
  guard('加载产品列表');
  return delay(paginate(MOCK_PRODUCTS, 1, 200));
}

export async function mockGetProduct(productId: string): Promise<Product> {
  guard('加载产品详情');
  const product = MOCK_PRODUCTS.find((p) => p.id === productId);
  if (!product) throw new ApiError({ code: 9006, message: '产品不存在', httpStatus: 404 });
  return delay(product);
}

export async function mockGetProductIntegration(productId: string): Promise<ProductIntegration> {
  guard('加载 SDK 接入信息');
  const product = MOCK_PRODUCTS.find((p) => p.id === productId);
  if (!product) throw new ApiError({ code: 9006, message: '产品不存在', httpStatus: 404 });
  return delay({
    product_id: product.id,
    // mock 根公钥：固定示例值（仅 mock 模式，真实值由服务端从 SIGNER_DEV_SEED_root 推导）
    root_public_key: 'Wxc7xv6To7mPUTIQM2Bxya3uyGJ79eBpe1U4OFkv4x0',
    base_url: 'http://localhost:3030/api/v1',
  });
}

export async function mockListProductVersions(productId: string): Promise<Paged<ProductVersion>> {
  guard('加载产品版本');
  const rows = MOCK_PRODUCT_VERSIONS
    .filter((v) => v.product_id === productId)
    .sort((a, b) => b.created_at - a.created_at);
  return delay(paginate(rows, 1, 50));
}

/** 软删除产品（mock）：存在 active/suspended 授权 → 9005 has_dependents。 */
export async function mockDeleteProduct(productId: string): Promise<void> {
  guard('归档产品');
  const product = MOCK_PRODUCTS.find((p) => p.id === productId);
  if (!product) throw new ApiError({ code: 9006, message: '产品不存在', httpStatus: 404 });
  const hasDependents = MOCK_LICENSES.some(
    (l) => l.product_id === productId && (l.status === 'active' || l.status === 'suspended'),
  );
  if (hasDependents) {
    throw new ApiError({ code: 9005, message: '存在未撤销的授权，无法归档', httpStatus: 409 });
  }
  const index = MOCK_PRODUCTS.findIndex((p) => p.id === productId);
  MOCK_PRODUCTS.splice(index, 1);
  return delay(undefined);
}

export async function mockPlans(productId?: string): Promise<Paged<Plan>> {
  guard('加载套餐列表');
  const plans = productId ? MOCK_PLANS.filter((p) => p.product_id === productId) : MOCK_PLANS;
  return delay(paginate(plans, 1, 200));
}

export async function mockCustomers(query: { q?: string; page?: number; limit?: number } = {}): Promise<Paged<Customer>> {
  guard('加载客户列表');
  const needle = (query.q ?? '').trim().toLowerCase();
  let rows = MOCK_CUSTOMERS;
  if (needle.length > 0) {
    rows = rows.filter((c) => [c.name ?? '', c.email ?? '', c.phone ?? ''].some((v) => v.toLowerCase().includes(needle)));
  }
  return delay(paginate(rows, query.page ?? 1, query.limit ?? 200));
}

/** 登记客户（mock）：就地 push 到单例，列表刷新可见。 */
export async function mockCreateCustomer(input: CustomerInput): Promise<Customer> {
  guard('登记客户');
  const seq = MOCK_CUSTOMER_SEQ++;
  const created: Customer = {
    id: `c-${Date.now().toString(16)}${seq}${'0123456789abcdef'[(seq * 3 + 5) % 16]}`,
    email: input.email ?? null,
    name: input.name ?? null,
    phone: input.phone ?? null,
    note: input.note ?? null,
    created_at: NOW,
  };
  MOCK_CUSTOMERS.push(created);
  return delay(created);
}

/** 编辑客户（mock）：就地更新单例。 */
export async function mockUpdateCustomer(customerId: string, input: CustomerInput): Promise<Customer> {
  guard('编辑客户');
  const target = MOCK_CUSTOMERS.find((c) => c.id === customerId);
  if (!target) throw new ApiError({ code: 9006, message: '客户不存在', httpStatus: 404 });
  Object.assign(target, {
    name: input.name !== undefined ? input.name : target.name,
    email: input.email !== undefined ? input.email : target.email,
    phone: input.phone !== undefined ? input.phone : target.phone,
    note: input.note !== undefined ? input.note : target.note,
  });
  return delay({ ...target });
}

/** 登记订单（mock）：就地 push 到单例，客户详情订单区刷新可见。 */
export async function mockCreateOrder(input: CreateOrderRequest): Promise<Order> {
  guard('登记订单');
  const seq = MOCK_ORDER_SEQ++;
  const created: Order = {
    id: `ord_01NEW${seq.toString().padStart(3, '0')}KQ7X`,
    product_id: input.product_id,
    customer_id: input.customer_id ?? null,
    source: input.source,
    external_order_no: input.external_order_no ?? null,
    amount_cents: input.amount_cents ?? null,
    currency: input.currency ?? null,
    note: input.note ?? null,
    created_at: NOW,
  };
  MOCK_ORDERS.push(created);
  return delay(created);
}

function matchesQuery(license: License, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (needle.length === 0) return true;
  const haystack = [
    license.id,
    license.order_id ?? '',
    license.note ?? '',
    license.status_reason ?? '',
    license.plan_snapshot.plan_name,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

const SORTERS: Record<
  NonNullable<ListLicensesQuery['sort']>,
  (a: License, b: License) => number
> = {
  created_at: (a, b) => a.created_at - b.created_at,
  updated_at: (a, b) => a.updated_at - b.updated_at,
  // 永久授权（null）排在最后：运营者关心的是「快到期的」，不是「不会到期的」
  expires_at: (a, b) => (a.expires_at ?? Number.MAX_SAFE_INTEGER) - (b.expires_at ?? Number.MAX_SAFE_INTEGER),
};

export async function mockLicenses(query: ListLicensesQuery): Promise<Paged<License>> {
  guard('加载授权列表');
  if (currentMode() === 'empty') {
    return delay({ items: [], total: 0, page: 1, limit: query.limit ?? 50, hasMore: false });
  }

  let rows = MOCK_LICENSES.slice();
  if (query.product_id !== undefined) rows = rows.filter((r) => r.product_id === query.product_id);
  if (query.status !== undefined) rows = rows.filter((r) => r.status === query.status);
  if (query.customer_id !== undefined) rows = rows.filter((r) => r.customer_id === query.customer_id);
  if (query.expires_before !== undefined) {
    const before = query.expires_before;
    rows = rows.filter((r) => r.expires_at !== null && r.expires_at < before);
  }
  if (query.q !== undefined) rows = rows.filter((r) => matchesQuery(r, query.q ?? ''));

  const sorter = SORTERS[query.sort ?? 'updated_at'];
  rows.sort(sorter);
  if ((query.order ?? 'desc') === 'desc') rows.reverse();

  return delay(paginate(rows, query.page ?? 1, query.limit ?? 50));
}

/* ── P-01 登录 / 会话（mock 模式） ── */

const MOCK_ADMIN = {
  id: '00000000-0000-0000-0000-000000000001',
  email: 'admin@example.com',
  name: 'Admin',
  role: 'owner' as const,
  is_active: true,
  last_login_at: null,
  created_at: 1_700_000_000,
};

export async function mockLogin(body: { email: string; password: string }): Promise<AdminUser> {
  guard('登录');
  // mock 也执行「失败不区分账号/密码」：只有这一组凭据能过
  if (body.email !== 'admin@example.com' || body.password !== 'AdminPassw0rd!') {
    throw new ApiError({ code: 9001, message: '邮箱或密码不正确', httpStatus: 401 });
  }
  return delay({ ...MOCK_ADMIN, last_login_at: Math.floor(Date.now() / 1000) });
}

export async function mockLogout(): Promise<void> {
  guard('登出');
  return delay(undefined);
}

export async function mockMe(): Promise<AdminUser> {
  guard('确认会话');
  return delay({ ...MOCK_ADMIN, last_login_at: Math.floor(Date.now() / 1000) });
}

/* ────────────────────────── 授权详情与生命周期（P-11 mock） ────────────────────────── */
/**
 * 与真实后端一致：mock 也**就地变更** MOCK_LICENSES（模块级单例），使「撤销→详情→状态变 revoked」
 * 在离线演示里自洽。生命周期动作同样返回 RevokeResult，逼出 UI 对 expected_offline_expiry_at 的展示。
 */
const DAY = 86_400;

function findMockLicense(id: string): License {
  const license = MOCK_LICENSES.find((l) => l.id === id);
  if (license === undefined) {
    throw new ApiError({ code: 9006, message: '授权不存在', httpStatus: 404 });
  }
  return license;
}

function mockCustomerOf(license: License): Customer | null {
  if (license.customer_id === null) return null;
  return MOCK_CUSTOMERS.find((c) => c.id === license.customer_id) ?? null;
}

function buildMockActivations(license: License): Activation[] {
  const count = license.activated_device_count;
  const live = license.status === 'active' || license.status === 'suspended';
  const out: Activation[] = [];
  for (let i = 0; i < count; i += 1) {
    const active = live;
    out.push({
      id: `act_${license.id.slice(-6)}_${String(i).padStart(2, '0')}`,
      license_id: license.id,
      product_id: license.product_id,
      device_hash: `device-hash-${(license.id + String(i)).slice(-12)}`,
      device_name: `设备 ${i + 1}`,
      status: active ? 'active' : 'deactivated',
      activated_at: license.issued_at + i * 3600,
      last_seen_at: live ? NOW - i * 1800 : license.revoked_at ?? license.suspended_at ?? NOW - DAY,
      deactivated_at: active ? null : (license.revoked_at ?? license.suspended_at ?? NOW - DAY),
      deactivated_by: active ? null : (license.status === 'revoked' ? 'admin' : 'user'),
    });
  }
  return out;
}

export async function mockGetLicense(licenseId: string): Promise<LicenseDetail> {
  guard('加载授权详情');
  const license = findMockLicense(licenseId);
  return delay({
    ...license,
    activations: buildMockActivations(license),
    customer: mockCustomerOf(license),
  });
}

export async function mockListLicenseActivations(
  licenseId: string,
  includeDeactivated: boolean,
): Promise<Activation[]> {
  guard('加载激活列表');
  const license = findMockLicense(licenseId);
  const acts = buildMockActivations(license);
  return delay(includeDeactivated ? acts : acts.filter((a) => a.status === 'active'));
}

export async function mockUpdateLicense(
  licenseId: string,
  body: { note?: string | null; customer_id?: string | null },
): Promise<License> {
  guard('更新授权');
  const license = findMockLicense(licenseId);
  const updated: License = {
    ...license,
    ...(body.note !== undefined ? { note: body.note } : {}),
    ...(body.customer_id !== undefined ? { customer_id: body.customer_id } : {}),
    updated_at: NOW,
  };
  Object.assign(license, updated);
  return delay(updated);
}

function mockRevokeOrSuspend(
  licenseId: string,
  reason: string,
  next: 'revoked' | 'suspended',
): RevokeResult {
  const license = findMockLicense(licenseId);
  if (license.status === next) {
    throw new ApiError({
      code: 9008,
      message: next === 'revoked' ? '该授权已撤销，不能再次撤销' : '该授权已处于暂停状态',
      httpStatus: 409,
    });
  }
  const grace = license.plan_snapshot.offline_grace_seconds;
  const updated: License = {
    ...license,
    status: next,
    status_reason: reason,
    revoked_at: next === 'revoked' ? NOW : license.revoked_at,
    suspended_at: next === 'suspended' ? NOW : license.suspended_at,
    updated_at: NOW,
  };
  Object.assign(license, updated);
  return {
    license: updated,
    expected_offline_expiry_at: NOW + grace,
    offline_grace_seconds: grace,
    online_effect: 'immediate_on_next_validate',
  };
}

export async function mockRevokeLicense(licenseId: string, reason: string): Promise<RevokeResult> {
  guard('撤销授权');
  return delay(mockRevokeOrSuspend(licenseId, reason, 'revoked'));
}

export async function mockSuspendLicense(licenseId: string, reason: string): Promise<RevokeResult> {
  guard('暂停授权');
  return delay(mockRevokeOrSuspend(licenseId, reason, 'suspended'));
}

export async function mockResumeLicense(licenseId: string): Promise<License> {
  guard('恢复授权');
  const license = findMockLicense(licenseId);
  if (license.status !== 'suspended') {
    throw new ApiError({ code: 9008, message: '只有被暂停的授权才能恢复', httpStatus: 409 });
  }
  const updated: License = {
    ...license,
    status: 'active',
    suspended_at: null,
    status_reason: null,
    updated_at: NOW,
  };
  Object.assign(license, updated);
  return delay(updated);
}

export async function mockRenewLicense(
  licenseId: string,
  body: { expires_at: number | null; refresh_plan_snapshot?: boolean; reason?: string | null },
): Promise<License> {
  guard('续期授权');
  const license = findMockLicense(licenseId);
  if (license.status === 'revoked') {
    throw new ApiError({ code: 9008, message: '已撤销的授权不可续期', httpStatus: 409 });
  }
  const updated: License = {
    ...license,
    status: 'active',
    expires_at: body.expires_at,
    revoked_at: null,
    suspended_at: null,
    status_reason: body.reason ?? null,
    updated_at: NOW,
  };
  Object.assign(license, updated);
  return delay(updated);
}

export async function mockDeactivateActivation(activationId: string, reason: string): Promise<Activation> {
  guard('解绑设备');
  const target = MOCK_ACTIVATIONS.find((a) => a.id === activationId);
  if (!target) throw new ApiError({ code: 9006, message: '激活记录不存在', httpStatus: 404 });
  const updated: Activation = {
    ...target,
    status: 'deactivated',
    deactivated_at: NOW,
    deactivated_by: 'admin',
    last_seen_at: target.last_seen_at,
  };
  Object.assign(target, updated);
  void reason;
  return delay(updated);
}

/* ────────────────────────── 兑换请求记录（P-22 mock） ────────────────────────── */
/**
 * 只读流水。刻意制造一个「同一码被尝试兑换 3 次」的幂等命中场景（同 digest、不同幂等键、
 * 状态 [200,409,409]），让前端「幂等命中标记」有真实数据可演示。
 * 其余行各自唯一 digest，绝大多数 200，穿插一条 404（code_not_found）展示失败态。
 */
function redeemDigest(n: number): string {
  // 确定性 64 hex（32 字节），避免 Math.random 让 mock 每次刷新都变
  let x = (n * 2654435761) >>> 0;
  let s = '';
  for (let i = 0; i < 16; i += 1) {
    x = (x * 1664525 + 1013904223) >>> 0;
    s += x.toString(16).padStart(8, '0');
  }
  return s.slice(0, 64);
}

const REDEEM_START = NOW - 30 * DAY;

const MOCK_REDEMPTIONS: RedemptionRequest[] = (() => {
  const rows: RedemptionRequest[] = [];
  const licenses = MOCK_LICENSES;
  let t = REDEEM_START;
  for (let i = 0; i < 52; i += 1) {
    const license = licenses[i % licenses.length]!;
    const failed = i === 17;
    rows.push({
      id: `rr_${(i + 1).toString().padStart(4, '0')}`,
      product_id: license.product_id,
      idempotency_key: `idem-${(i + 1).toString().padStart(4, '0')}-${redeemDigest(i).slice(0, 8)}`,
      request_digest: failed ? redeemDigest(900 + i) : redeemDigest(i),
      license_id: failed ? null : license.id,
      status_code: failed ? 404 : 200,
      response_body: failed
        ? JSON.stringify({ code: 2001, message: '授权码不存在或已失效' })
        : JSON.stringify({ code: 0, data: { license_id: license.id, payload: 'LICENSE-ENVELOPE-OMITTED' } }),
      created_at: t,
      expires_at: t + DAY,
    });
    t += 3600 + (i % 7) * 600;
  }
  // 幂等命中场景：同一码被尝试兑换 3 次（1 成功 + 2 回放）
  const dupLicense = licenses[2]!;
  const dupDigest = `d00d${redeemDigest(777).slice(4)}`;
  const dupBase = NOW - 3 * DAY;
  rows.push(
    {
      id: 'rr_dup01',
      product_id: dupLicense.product_id,
      idempotency_key: 'idem-dup-001',
      request_digest: dupDigest,
      license_id: dupLicense.id,
      status_code: 200,
      response_body: JSON.stringify({ code: 0, data: { license_id: dupLicense.id } }),
      created_at: dupBase,
      expires_at: dupBase + DAY,
    },
    {
      id: 'rr_dup02',
      product_id: dupLicense.product_id,
      idempotency_key: 'idem-dup-002',
      request_digest: dupDigest,
      license_id: dupLicense.id,
      status_code: 409,
      response_body: JSON.stringify({ code: 2002, message: '授权码已兑换' }),
      created_at: dupBase + 30,
      expires_at: dupBase + DAY + 30,
    },
    {
      id: 'rr_dup03',
      product_id: dupLicense.product_id,
      idempotency_key: 'idem-dup-003',
      request_digest: dupDigest,
      license_id: dupLicense.id,
      status_code: 409,
      response_body: JSON.stringify({ code: 2002, message: '授权码已兑换' }),
      created_at: dupBase + 90,
      expires_at: dupBase + DAY + 90,
    },
  );
  return rows;
})();

export async function mockListRedemptionRequests(
  query: { page?: number; limit?: number } = {},
): Promise<Paged<RedemptionRequest>> {
  if (currentMode() === 'empty') {
    return delay({ items: [], total: 0, page: query.page ?? 1, limit: query.limit ?? 50, hasMore: false });
  }
  guard('加载兑换请求');
  // 倒序：最近的兑换尝试在最前（与审计日志一致）
  const sorted = [...MOCK_REDEMPTIONS].sort((a, b) => b.created_at - a.created_at);
  return delay(paginate(sorted, query.page ?? 1, query.limit ?? 50));
}

/* ────────────────────────── 功能开关注册表（P-21） ────────────────────────── */

/**
 * 功能开关键注册表种子数据。键名与 MOCK_PLANS 里引用的 entitlements 键对齐，
 * 这样「被套餐引用 → 删除返回 9005」的保护在演示里真实可触发。
 * 产品 ID 取自 ./data 的 MOCK_PRODUCTS（aototokenpool / shotpanel / ledgerlens）。
 */
const AOTO = '7c1f4a92-3b6e-4d21-9f08-5a2c8e1d4b60';
const SHOT = 'a4e0d517-88c2-4f3a-b1d6-2e9f7c035a18';
const LEDGER = 'd93b6c04-71af-4e58-8c17-6b4a0f2d9e33';

let MOCK_ENTITLEMENT_KEYS: EntitlementKey[] = [
  { id: 'ek_aoto_concurrent_pools', product_id: AOTO, key: 'concurrent_pools', value_type: 'integer', display_name: '并发 Token 池数量', default_value: 1, created_at: NOW - 400 * DAY },
  { id: 'ek_aoto_priority_queue', product_id: AOTO, key: 'priority_queue', value_type: 'boolean', display_name: '优先队列', default_value: false, created_at: NOW - 399 * DAY },
  { id: 'ek_aoto_export_csv', product_id: AOTO, key: 'export_csv', value_type: 'boolean', display_name: '导出 CSV', default_value: true, created_at: NOW - 398 * DAY },
  { id: 'ek_shot_max_annotations', product_id: SHOT, key: 'max_annotations', value_type: 'integer', display_name: '最大批注数', default_value: 50, created_at: NOW - 200 * DAY },
  { id: 'ek_shot_team_sharing', product_id: SHOT, key: 'team_sharing', value_type: 'boolean', display_name: '团队共享', default_value: false, created_at: NOW - 199 * DAY },
  { id: 'ek_ledger_compare_accounts', product_id: LEDGER, key: 'compare_accounts', value_type: 'integer', display_name: '可对比账套数', default_value: 2, created_at: NOW - 600 * DAY },
  { id: 'ek_ledger_audit_log', product_id: LEDGER, key: 'audit_log', value_type: 'boolean', display_name: '审计日志', default_value: true, created_at: NOW - 599 * DAY },
];

/** 被任一套餐引用的开关键（删除时返回 9005 has_dependents）。键名取自 MOCK_PLANS.entitlements */
const REFERENCED_KEYS = new Set<string>(['concurrent_pools', 'priority_queue', 'export_csv', 'audit_log']);

const KEY_PATTERN = /^[a-z0-9][a-z0-9_]{1,62}$/;

export async function mockListEntitlementKeys(productId?: string): Promise<EntitlementKey[]> {
  if (currentMode() === 'empty') return delay([]);
  guard('加载功能开关注册表');
  const rows = (productId ? MOCK_ENTITLEMENT_KEYS.filter((k) => k.product_id === productId) : MOCK_ENTITLEMENT_KEYS).sort(
    (a, b) => a.created_at - b.created_at,
  );
  return delay(rows);
}

export async function mockCreateEntitlementKey(
  productId: string,
  body: CreateEntitlementKeyRequest,
): Promise<EntitlementKey> {
  guard('注册功能开关键');
  if (!KEY_PATTERN.test(body.key)) {
    throw new ApiError({ code: 9007, message: 'key 必须符合 ^[a-z0-9][a-z0-9_]{1,62}$', httpStatus: 400 });
  }
  const dup = MOCK_ENTITLEMENT_KEYS.find((k) => k.product_id === productId && k.key === body.key);
  if (dup) {
    throw new ApiError({ code: 9008, message: '该产品已存在同名开关键', httpStatus: 409 });
  }
  const created: EntitlementKey = {
    id: `ek_${productId.slice(0, 4)}_${body.key}`,
    product_id: productId,
    key: body.key,
    value_type: body.value_type,
    display_name: body.display_name,
    default_value: body.default_value ?? null,
    created_at: NOW,
  };
  MOCK_ENTITLEMENT_KEYS = [...MOCK_ENTITLEMENT_KEYS, created];
  return delay(created);
}

export async function mockUpdateEntitlementKey(
  id: string,
  body: UpdateEntitlementKeyRequest,
): Promise<EntitlementKey> {
  guard('修改功能开关键');
  const idx = MOCK_ENTITLEMENT_KEYS.findIndex((k) => k.id === id);
  if (idx === -1) throw new ApiError({ code: 9006, message: '开关键不存在', httpStatus: 404 });
  const current = MOCK_ENTITLEMENT_KEYS[idx];
  const next: EntitlementKey = {
    ...current,
    display_name: body.display_name ?? current.display_name,
    default_value: body.default_value === undefined ? current.default_value : body.default_value,
  };
  MOCK_ENTITLEMENT_KEYS = MOCK_ENTITLEMENT_KEYS.map((k, i) => (i === idx ? next : k));
  return delay(next);
}

export async function mockDeleteEntitlementKey(id: string): Promise<void> {
  guard('删除功能开关键');
  const target = MOCK_ENTITLEMENT_KEYS.find((k) => k.id === id);
  if (!target) throw new ApiError({ code: 9006, message: '开关键不存在', httpStatus: 404 });
  if (REFERENCED_KEYS.has(target.key)) {
    throw new ApiError({ code: 9005, message: 'has_dependents', httpStatus: 409 });
  }
  MOCK_ENTITLEMENT_KEYS = MOCK_ENTITLEMENT_KEYS.filter((k) => k.id !== id);
  return delay(undefined);
}

/* ────────────────────────── 管理员手动创建授权（P-12） ────────────────────────── */

let MOCK_LICENSE_SEQ = MOCK_LICENSES.length;

function buildPlanSnapshot(plan: Plan): PlanSnapshot {
  return {
    plan_id: plan.id,
    plan_code: plan.code,
    plan_name: plan.name,
    duration_days: plan.duration_days ?? null,
    max_devices: plan.max_devices ?? 0,
    offline_grace_seconds: plan.offline_grace_seconds ?? 0,
    version_constraint: plan.version_constraint,
    entitlements: plan.entitlements,
    snapshot_at: NOW,
  };
}

export async function mockCreateLicense(body: CreateLicenseRequest): Promise<License> {
  guard('创建授权');
  const product = MOCK_PRODUCTS.find((p) => p.id === body.product_id);
  if (!product) throw new ApiError({ code: 9006, message: '产品不存在', httpStatus: 404 });
  const plan = MOCK_PLANS.find((p) => p.id === body.plan_id && p.product_id === body.product_id);
  if (!plan) throw new ApiError({ code: 9006, message: '套餐不存在或不属于该产品', httpStatus: 404 });
  const customer = body.customer_id ? MOCK_CUSTOMERS.find((c) => c.id === body.customer_id) : null;

  const seq = MOCK_LICENSE_SEQ++;
  const issuedAt = NOW;
  const created: License = {
    id: `lic_01NEW${seq.toString().padStart(3, '0')}KQ7X${Math.floor(NOW % 10000).toString(16).toUpperCase().padStart(4, '0')}`,
    product_id: product.id,
    plan_id: plan.id,
    license_code_id: null,
    customer_id: customer?.id ?? null,
    order_id: body.order_id ?? null,
    status: 'active',
    plan_snapshot: buildPlanSnapshot(plan),
    issued_at: issuedAt,
    expires_at: body.expires_at ?? null,
    revoked_at: null,
    suspended_at: null,
    status_reason: null,
    activated_device_count: 0,
    note: body.note ?? null,
    created_at: issuedAt,
    updated_at: issuedAt,
  };
  // 就地追加到单例，保证列表页刷新后能看到刚创建的授权（离线演示自洽）
  MOCK_LICENSES.push(created);
  return delay(created);
}

/* ────────────────────────── 激活设备（P-13 mock） ────────────────────────── */

export async function mockListActivations(query: {
  page?: number;
  limit?: number;
  status?: string;
  license_id?: string;
} = {}): Promise<Paged<Activation>> {
  guard('加载激活设备');
  let rows = MOCK_ACTIVATIONS;
  if (query.status) rows = rows.filter((a) => a.status === query.status);
  if (query.license_id) rows = rows.filter((a) => a.license_id === query.license_id);
  // 与真实端点一致：最近心跳优先
  rows = [...rows].sort((a, b) => b.last_seen_at - a.last_seen_at);
  return delay(paginate(rows, query.page ?? 1, query.limit ?? 20));
}

/* ────────────────────────── 签名密钥（P-15 mock） ────────────────────────── */

export async function mockListSigningKeys(productId?: string): Promise<SigningKey[]> {
  guard('加载签名密钥');
  return delay(productId ? MOCK_SIGNING_KEYS.filter((k) => k.product_id === productId) : MOCK_SIGNING_KEYS);
}

let MOCK_SIGNING_SEQ = 0;

function b64u(seed: number): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
  return Array.from({ length: 43 }, (_, i) => alphabet[(seed * 17 + i * 31) % 64]).join('');
}

/** 轮换叶密钥：旧 active leaf → retiring（保留期 90 天），新 leaf → active。 */
export async function mockRotateSigningKey(
  productId: string,
  body: { reason: string; retiring_until?: number | null },
): Promise<RotateKeyResult> {
  guard('轮换签名密钥');
  const product = MOCK_PRODUCTS.find((p) => p.id === productId);
  if (!product) throw new ApiError({ code: 9006, message: '产品不存在', httpStatus: 404 });
  const retiring = MOCK_SIGNING_KEYS.find((k) => k.product_id === productId && k.status === 'active' && k.role === 'leaf') ?? null;

  const seq = MOCK_SIGNING_SEQ++;
  const month = new Date().toISOString().slice(0, 7);
  const now = NOW;
  const created: SigningKey = {
    id: `sk_01NEW${seq.toString().padStart(3, '0')}KQ7X`,
    key_id: `lh-${product.slug}-${month}-${String(seq + 1).padStart(2, '0')}`,
    product_id: productId,
    algorithm: 'Ed25519',
    role: 'leaf',
    status: 'active',
    public_key_b64u: b64u(seq + 3),
    has_private_key: true,
    not_before: now,
    not_after: null,
    retired_at: null,
    created_at: now,
  };
  MOCK_SIGNING_KEYS.push(created);
  if (retiring) {
    Object.assign(retiring, { status: 'retiring' as const });
  }
  void body;
  return delay({
    new_key: created,
    retiring_key: retiring,
    requires_root_signature: true,
  });
}

/** 应急吊销：不走 retiring 缓冲，直接 compromised。 */
export async function mockCompromiseSigningKey(
  signingKeyId: string,
  reason: string,
): Promise<CompromiseKeyResult> {
  guard('应急吊销签名密钥');
  const target = MOCK_SIGNING_KEYS.find((k) => k.id === signingKeyId);
  if (!target) throw new ApiError({ code: 9006, message: '签名密钥不存在', httpStatus: 404 });
  const updated: SigningKey = { ...target, status: 'compromised' };
  Object.assign(target, updated);
  void reason;
  return delay({
    key: updated,
    estimated_affected_licenses: 42,
    requires_root_signature: true,
  });
}

/** 下线 retiring 密钥。mock 放行（真实后端按保留期校验，未满返回 9009）。 */
export async function mockRetireSigningKey(signingKeyId: string): Promise<SigningKey> {
  guard('下线签名密钥');
  const target = MOCK_SIGNING_KEYS.find((k) => k.id === signingKeyId);
  if (!target) throw new ApiError({ code: 9006, message: '签名密钥不存在', httpStatus: 404 });
  if (target.status !== 'retiring') {
    throw new ApiError({ code: 9009, message: '仅保留期内的 retiring 密钥可下线', httpStatus: 409 });
  }
  const updated: SigningKey = { ...target, status: 'retired', retired_at: NOW };
  Object.assign(target, updated);
  return delay(updated);
}

/* ────────────────────────── 审计日志（P-18 mock） ────────────────────────── */

export async function mockListAuditEvents(query: { page?: number; limit?: number } = {}): Promise<Paged<AuditEvent>> {
  guard('加载审计日志');
  const rows = [...MOCK_AUDIT_EVENTS].sort((a, b) => b.created_at - a.created_at);
  return delay(paginate(rows, query.page ?? 1, query.limit ?? 20));
}

/** mock：单条删除审计事件。过滤掉目标 uuid，返回 void。 */
export async function mockDeleteAuditEvent(id: string): Promise<void> {
  guard('删除审计事件');
  const idx = MOCK_AUDIT_EVENTS.findIndex((e) => e.id === id);
  if (idx >= 0) MOCK_AUDIT_EVENTS.splice(idx, 1);
  return delay(undefined);
}

/** mock：批量删除审计事件。过滤掉目标 uuid 集合，返回实际删除条数。 */
export async function mockDeleteAuditEvents(ids: string[]): Promise<{ deleted: number }> {
  guard('批量删除审计事件');
  const set = new Set(ids);
  const before = MOCK_AUDIT_EVENTS.length;
  for (let i = MOCK_AUDIT_EVENTS.length - 1; i >= 0; i--) {
    if (set.has(MOCK_AUDIT_EVENTS[i].id)) MOCK_AUDIT_EVENTS.splice(i, 1);
  }
  return delay({ deleted: before - MOCK_AUDIT_EVENTS.length });
}

/* ────────────────────────── 订单（P-14 mock） ────────────────────────── */

export async function mockListOrders(customerId: string): Promise<Paged<Order>> {
  guard('加载订单');
  const rows = MOCK_ORDERS.filter((o) => o.customer_id === customerId).sort((a, b) => b.created_at - a.created_at);
  return delay(paginate(rows, 1, 200));
}

/* ────────────────────────── 批次与库存（P-06/P-08 mock） ────────────────────────── */

export async function mockListCodeBatches(productId?: string): Promise<Paged<CodeBatch>> {
  guard('加载批次');
  const rows = (productId ? MOCK_CODE_BATCHES.filter((b) => b.product_id === productId) : MOCK_CODE_BATCHES).sort((a, b) => b.created_at - a.created_at);
  return delay(paginate(rows, 1, 200));
}

export async function mockGetCodeBatch(batchId: string): Promise<CodeBatch> {
  guard('加载批次详情');
  const batch = MOCK_CODE_BATCHES.find((b) => b.id === batchId);
  if (!batch) throw new ApiError({ code: 9006, message: '批次不存在', httpStatus: 404 });
  return delay({ ...batch, stats: batch.stats ?? { available: 0, exported: 0, redeemed: 0, void: 0 } });
}

let MOCK_BATCH_SEQ = MOCK_CODE_BATCHES.length;
let MOCK_CODE_SEQ = MOCK_LICENSE_CODES.length;

/**
 * 批次明文内存（对齐真实后端 BATCH_PLAINTEXT）：创建批次时生成 quantity 条明文，
 * 导出时流出、导出后删除。明文永不落 mock 数据源（与真实红线一致）。
 */
const MOCK_BATCH_PLAINTEXT = new Map<string, string[]>();

export async function mockCreateCodeBatch(
  productId: string,
  planId: string,
  quantity: number,
  opts: { note?: string; idempotencyKey: string },
): Promise<CodeBatch> {
  guard('创建批次');
  const product = MOCK_PRODUCTS.find((p) => p.id === productId);
  if (!product) throw new ApiError({ code: 9006, message: '产品不存在', httpStatus: 404 });
  const plan = MOCK_PLANS.find((p) => p.id === planId && p.product_id === productId);
  if (!plan) throw new ApiError({ code: 9006, message: '套餐不存在或不属于该产品', httpStatus: 404 });
  const seq = MOCK_BATCH_SEQ++;
  const batchNo = `B2026-08-${String(seq + 1).padStart(3, '0')}`;
  const created: CodeBatch = {
    id: `cb_01NEW${seq.toString().padStart(3, '0')}KQ7X${Math.floor(NOW % 10000).toString(16).toUpperCase().padStart(4, '0')}`,
    product_id: productId,
    plan_id: planId,
    batch_no: batchNo,
    quantity,
    note: opts.note ?? null,
    export_count: 0,
    first_exported_at: null,
    exported_by: null,
    stats: { available: quantity, exported: 0, redeemed: 0, void: 0 },
    created_at: NOW,
  };
  MOCK_CODE_BATCHES.push(created);
  // 同步生成授权码：明文存内存（导出时流出），code_mask 为明文脱敏（头 5 + 尾 5 可见，与真实后端一致）
  const plaintexts: string[] = [];
  for (let i = 0; i < quantity; i++) {
    const codeSeq = MOCK_CODE_SEQ++;
    const tail = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'[(codeSeq * 3 + 7) % 28];
    const mask = codeMask(codeSeq);
    const plain = mockPlainFromMask(mask, codeSeq);
    plaintexts.push(plain);
    MOCK_LICENSE_CODES.push({
      id: `code_01NEW${codeSeq.toString().padStart(3, '0')}KQ7X${tail}`,
      product_id: productId,
      plan_id: planId,
      batch_id: created.id,
      code_mask: mask,
      status: 'available',
      redeemed_at: null,
      void_reason: null,
      created_at: NOW,
    });
  }
  MOCK_BATCH_PLAINTEXT.set(created.id, plaintexts);
  return delay(created);
}

/**
 * 导出批次明文（mock）：与真实后端同语义——
 * 1. 明文不落库，导出时按 available 码现场生成（对齐「明文只在一次性导出时流出」红线）；
 * 2. 已导出（export_count > 0）拒绝二次导出（对齐 BATCH_ALREADY_EXPORTED → 9007）；
 * 3. 导出后批次 export_count/first_exported_at/exported_by/stats 与码状态就地更新，列表/详情刷新可见。
 * CSV 格式对齐真实后端：UTF-8 BOM + `code,plan_name,expires_at,note`。
 */
export async function mockExportCodeBatchCsv(batchId: string): Promise<string> {
  guard('导出批次明文');
  const batch = MOCK_CODE_BATCHES.find((b) => b.id === batchId);
  if (!batch) throw new ApiError({ code: 9006, message: '批次不存在', httpStatus: 404 });
  if ((batch.export_count ?? 0) > 0) {
    throw new ApiError({ code: 9007, message: '批次已导出，明文已销毁（一次性导出）', httpStatus: 409 });
  }
  const codes = MOCK_LICENSE_CODES.filter((c) => c.batch_id === batchId && c.status === 'available');
  const plan = MOCK_PLANS.find((p) => p.id === batch.plan_id);
  // 明文优先取创建时存的内存（对齐 BATCH_PLAINTEXT）；种子批次（无内存）从掩码反推（头 5 + 尾 5 已知，中间确定性填充）
  let plaintexts = MOCK_BATCH_PLAINTEXT.get(batchId);
  if (!plaintexts || plaintexts.length !== codes.length) {
    plaintexts = codes.map((code, i) => mockPlainFromMask(code.code_mask, i + 1));
  }
  const rows = plaintexts.map((plain) => `${plain},${plan?.name ?? ''},,`);
  // 就地更新批次：一次性导出
  Object.assign(batch, {
    export_count: 1,
    first_exported_at: NOW,
    exported_by: MOCK_ADMIN.email,
    stats: {
      available: Math.max(0, (batch.stats?.available ?? 0) - codes.length),
      exported: (batch.stats?.exported ?? 0) + codes.length,
      redeemed: batch.stats?.redeemed ?? 0,
      void: batch.stats?.void ?? 0,
    },
  });
  // 码状态 available → exported（与真实后端一致）
  for (const code of codes) Object.assign(code, { status: 'exported' as const });
  // 明文已流出，内存即删（对齐真实 BATCH_PLAINTEXT.delete）
  MOCK_BATCH_PLAINTEXT.delete(batchId);
  return `\uFEFFcode,plan_name,expires_at,note\n${rows.join('\n')}\n`;
}

export async function mockListLicenseCodes(query: {
  product_id?: string;
  batch_id?: string;
  status?: string;
  page?: number;
  limit?: number;
} = {}): Promise<Paged<LicenseCode>> {
  guard('加载授权码');
  let rows = MOCK_LICENSE_CODES;
  if (query.product_id) rows = rows.filter((c) => c.product_id === query.product_id);
  if (query.batch_id) rows = rows.filter((c) => c.batch_id === query.batch_id);
  if (query.status) rows = rows.filter((c) => c.status === query.status);
  rows = [...rows].sort((a, b) => b.created_at - a.created_at);
  return delay(paginate(rows, query.page ?? 1, query.limit ?? 20));
}

/** 作废授权码（mock）：available/exported → void；已兑换/已作废拒绝。 */
export async function mockVoidLicenseCode(codeId: string, reason: string): Promise<LicenseCode> {
  guard('作废授权码');
  const target = MOCK_LICENSE_CODES.find((c) => c.id === codeId);
  if (!target) throw new ApiError({ code: 9006, message: '授权码不存在', httpStatus: 404 });
  if (target.status === 'redeemed') {
    throw new ApiError({ code: 9007, message: '已兑换的授权码不能作废，请撤销对应授权', httpStatus: 409 });
  }
  if (target.status === 'void') {
    throw new ApiError({ code: 9007, message: '授权码已是作废状态', httpStatus: 409 });
  }
  const updated: LicenseCode = { ...target, status: 'void', void_reason: reason };
  Object.assign(target, updated);
  return delay(updated);
}

/** 批量作废（mock，原子）：任一条已兑换则整体拒绝；ids ≤ 200。 */
export async function mockBulkVoidLicenseCodes(ids: string[], reason: string): Promise<{ voided: number }> {
  guard('批量作废授权码');
  if (ids.length === 0 || ids.length > 200) {
    throw new ApiError({ code: 1002, message: 'ids 数量须在 1~200 之间', httpStatus: 400 });
  }
  const targets = MOCK_LICENSE_CODES.filter((c) => ids.includes(c.id));
  if (targets.length !== ids.length) throw new ApiError({ code: 9006, message: '存在不存在的授权码', httpStatus: 404 });
  if (targets.some((c) => c.status === 'redeemed')) {
    throw new ApiError({ code: 9007, message: '含已兑换的授权码，整体拒绝', httpStatus: 409 });
  }
  for (const target of targets) {
    Object.assign(target, { status: 'void' as const, void_reason: reason });
  }
  return delay({ voided: ids.length });
}

/* ────────────────────────── 产品 / 套餐写操作 mock ────────────────────────── */

let MOCK_PRODUCT_SEQ = 0;

export async function mockCreateProduct(data: {
  slug: string;
  name: string;
  description?: string | null;
}): Promise<Product> {
  guard('创建产品');
  if (MOCK_PRODUCTS.some((p) => p.slug === data.slug)) {
    throw new ApiError({ code: 9008, message: '产品 slug 已存在', httpStatus: 409 });
  }
  const seq = MOCK_PRODUCT_SEQ++;
  const created: Product = {
    id: `prd_01NEW${seq.toString().padStart(3, '0')}KQ7X${Math.floor(NOW % 10000).toString(16).toUpperCase().padStart(4, '0')}`,
    slug: data.slug,
    name: data.name,
    description: data.description ?? null,
    is_active: true,
    revocation_list_version: 0,
    created_at: NOW,
    updated_at: NOW,
  };
  MOCK_PRODUCTS.push(created);
  return delay(created);
}

export async function mockUpdateProduct(
  id: string,
  data: { name?: string; description?: string | null; is_active?: boolean },
): Promise<Product> {
  guard('更新产品');
  const target = MOCK_PRODUCTS.find((p) => p.id === id);
  if (!target) throw new ApiError({ code: 9006, message: '产品不存在', httpStatus: 404 });
  const updated: Product = { ...target, ...data, updated_at: NOW };
  Object.assign(target, updated);
  return delay(updated);
}

export async function mockCreatePlan(
  productId: string,
  data: {
    code: string;
    name: string;
    max_devices: number;
    duration_days?: number | null;
    offline_grace_seconds?: number;
    version_constraint?: string | null;
    entitlements?: Record<string, EntitlementValue>;
  },
): Promise<Plan> {
  guard('创建套餐');
  const product = MOCK_PRODUCTS.find((p) => p.id === productId);
  if (!product) throw new ApiError({ code: 9006, message: '产品不存在', httpStatus: 404 });
  if (MOCK_PLANS.some((p) => p.code === data.code && p.product_id === productId)) {
    throw new ApiError({ code: 9008, message: '套餐 code 在该产品下已存在', httpStatus: 409 });
  }
  const created: Plan = {
    id: `p-${product.slug}-${data.code}`,
    product_id: productId,
    code: data.code,
    name: data.name,
    duration_days: data.duration_days ?? null,
    max_devices: data.max_devices,
    offline_grace_seconds: data.offline_grace_seconds ?? 259_200,
    version_constraint: data.version_constraint ?? null,
    entitlements: data.entitlements ?? {},
    is_active: true,
    created_at: NOW,
  };
  MOCK_PLANS.push(created);
  return delay(created);
}

export async function mockUpdatePlan(
  planId: string,
  data: {
    name?: string;
    max_devices?: number;
    duration_days?: number | null;
    offline_grace_seconds?: number;
    version_constraint?: string | null;
    is_active?: boolean;
    entitlements?: Record<string, EntitlementValue>;
  },
): Promise<Plan> {
  guard('更新套餐');
  const target = MOCK_PLANS.find((p) => p.id === planId);
  if (!target) throw new ApiError({ code: 9006, message: '套餐不存在', httpStatus: 404 });
  const updated: Plan = { ...target, ...data };
  Object.assign(target, updated);
  return delay(updated);
}

export async function mockGetPlan(planId: string): Promise<Plan> {
  guard('加载套餐');
  const plan = MOCK_PLANS.find((p) => p.id === planId);
  if (!plan) throw new ApiError({ code: 9006, message: '套餐不存在', httpStatus: 404 });
  return delay(plan);
}

/* ────────────────────────── IAM（P-16/P-17/P-23 mock） ────────────────────────── */

let MOCK_ADMIN_SEQ = MOCK_ADMIN_USERS.length;

export async function mockListAdminUsers(): Promise<AdminUser[]> {
  guard('加载管理员');
  return delay([...MOCK_ADMIN_USERS]);
}

export async function mockCreateAdminUser(data: { email: string; name: string; password: string; role: string }): Promise<AdminUser> {
  guard('创建管理员');
  if (MOCK_ADMIN_USERS.some((u) => u.email === data.email)) {
    throw new ApiError({ code: 9008, message: '邮箱已存在', httpStatus: 409 });
  }
  const seq = MOCK_ADMIN_SEQ++;
  const created: AdminUser = {
    id: `usr_01NEW${seq.toString().padStart(3, '0')}KQ7X`,
    email: data.email,
    name: data.name,
    role: data.role as AdminUser['role'],
    is_active: true,
    last_login_at: null,
    created_at: NOW,
  };
  MOCK_ADMIN_USERS.push(created);
  void data.password;
  return delay(created);
}

/**
 * 更新管理员（mock）：改角色 / 停用启用。
 * 与真实后端同纪律：不能停用 / 降级最后一个 owner（9010 last_owner_protected）。
 */
export async function mockUpdateAdminUser(
  adminUserId: string,
  data: { name?: string; role?: string; is_active?: boolean; reset_password?: string },
): Promise<AdminUser> {
  guard('更新管理员');
  const target = MOCK_ADMIN_USERS.find((u) => u.id === adminUserId);
  if (!target) throw new ApiError({ code: 9006, message: '管理员不存在', httpStatus: 404 });
  const ownerCount = MOCK_ADMIN_USERS.filter((u) => u.role === 'owner' && u.is_active).length;
  const isLastOwner = target.role === 'owner' && target.is_active && ownerCount <= 1;
  const demotingOwner = data.role !== undefined && data.role !== 'owner' && target.role === 'owner';
  const deactivatingOwner = data.is_active === false && target.role === 'owner';
  if (isLastOwner && (demotingOwner || deactivatingOwner)) {
    throw new ApiError({ code: 9010, message: '不能停用或降级最后一个 owner', httpStatus: 409 });
  }
  const updated: AdminUser = {
    ...target,
    ...(data.name !== undefined ? { name: data.name } : {}),
    ...(data.role !== undefined ? { role: data.role as AdminUser['role'] } : {}),
    ...(data.is_active !== undefined ? { is_active: data.is_active } : {}),
  };
  Object.assign(target, updated);
  void data.reset_password;
  return delay(updated);
}

/**
 * 删除管理员（mock）：软删除 = 停用 + 撤销会话。
 * 与真实后端同纪律：最后一个 owner 不可删除。
 */
export async function mockDeleteAdminUser(adminUserId: string): Promise<void> {
  guard('删除管理员');
  const target = MOCK_ADMIN_USERS.find((u) => u.id === adminUserId);
  if (!target) throw new ApiError({ code: 9006, message: '管理员不存在', httpStatus: 404 });
  const ownerCount = MOCK_ADMIN_USERS.filter((u) => u.role === 'owner' && u.is_active).length;
  const isLastOwner = target.role === 'owner' && target.is_active && ownerCount <= 1;
  if (isLastOwner) {
    throw new ApiError({ code: 9010, message: '不能删除最后一个 owner', httpStatus: 409 });
  }
  target.is_active = false;
  return delay(undefined);
}

export async function mockListApiKeys(): Promise<ApiKey[]> {
  guard('加载 API Key');
  return delay([...MOCK_API_KEYS]);
}

let MOCK_APIKEY_SEQ = MOCK_API_KEYS.length;
let MOCK_CUSTOMER_SEQ = MOCK_CUSTOMERS.length;
let MOCK_ORDER_SEQ = MOCK_ORDERS.length;

/** 创建 API Key（mock）：明文只在返回值中出现一次。 */
export async function mockCreateApiKey(data: {
  name: string;
  role: string;
  expires_at?: number | null;
  scopes?: string[];
}): Promise<CreateApiKeyResult> {
  guard('创建 API Key');
  const seq = MOCK_APIKEY_SEQ++;
  const created: ApiKey = {
    id: `key_01NEW${seq.toString().padStart(3, '0')}KQ7X`,
    name: data.name,
    key_prefix: `lhk_${'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[(seq * 7 + 3) % 32]}${'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[(seq * 13 + 9) % 32]}`,
    role: data.role as ApiKey['role'],
    scopes: data.scopes ?? [],
    last_used_at: null,
    expires_at: data.expires_at ?? null,
    created_at: NOW,
  };
  MOCK_API_KEYS.push(created);
  const plaintext = `${created.key_prefix}.${Array.from({ length: 40 }, (_, i) => 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[(seq * 31 + i * 17) % 62]).join('')}`;
  return delay({ api_key: created, plaintext });
}

/** 撤销 API Key（mock）：从列表移除（真实后端 list 也过滤已吊销）。 */
export async function mockRevokeApiKey(apiKeyId: string): Promise<void> {
  guard('撤销 API Key');
  const index = MOCK_API_KEYS.findIndex((k) => k.id === apiKeyId);
  if (index === -1) throw new ApiError({ code: 9006, message: 'API Key 不存在', httpStatus: 404 });
  MOCK_API_KEYS.splice(index, 1);
  return delay(undefined);
}

export async function mockListSessions(): Promise<Session[]> {
  guard('加载会话');
  return delay([...MOCK_SESSIONS]);
}

/** 踢下线（mock）：移除指定会话。当前会话（is_current）不可踢。 */
export async function mockRevokeSession(sessionId: string): Promise<void> {
  guard('踢下线');
  const target = MOCK_SESSIONS.find((s) => s.id === sessionId);
  if (!target) throw new ApiError({ code: 9006, message: '会话不存在', httpStatus: 404 });
  if (target.is_current) throw new ApiError({ code: 9007, message: '不能踢掉当前会话', httpStatus: 409 });
  const index = MOCK_SESSIONS.findIndex((s) => s.id === sessionId);
  MOCK_SESSIONS.splice(index, 1);
  return delay(undefined);
}

/** 全部登出（mock）：撤销除当前会话外的全部会话。 */
export async function mockRevokeAllSessions(): Promise<{ revoked: number }> {
  guard('全部登出');
  const keepCurrent = MOCK_SESSIONS.filter((s) => s.is_current);
  const revoked = MOCK_SESSIONS.length - keepCurrent.length;
  MOCK_SESSIONS.length = 0;
  MOCK_SESSIONS.push(...keepCurrent);
  return delay({ revoked });
}

/* ── 安全：IP 黑名单（mock，owner 专属） ── */

/**
 * 可变内存表，模拟后端 `ip_blacklist` 表的当前生效记录。
 * 默认塞两条演示数据（一条 1h 临时封禁、一条永久），让「解封」交互在 mock 下可验收。
 */
const MOCK_IP_BLACKLIST: IpBlacklistEntry[] = [
  {
    ip: '203.0.113.45',
    reason: 'login_bruteforce',
    created_at: NOW - 600,
    expires_at: NOW + 1740,
    expired: false,
  },
  {
    ip: '198.51.100.23',
    reason: 'redeem_ip_flood',
    created_at: NOW - 3600,
    expires_at: null,
    expired: false,
  },
];

/** 列出当前生效的 IP 封禁（mock）。 */
export async function mockListIpBlacklist(): Promise<IpBlacklistEntry[]> {
  guard('加载 IP 黑名单');
  const now = NOW;
  return delay(
    MOCK_IP_BLACKLIST.map((entry) => ({
      ...entry,
      expired: entry.expires_at !== null && entry.expires_at <= now,
    })),
  );
}

/** 人工解封某个 IP（mock）。IP 不存在视为幂等成功（与后端语义一致）。 */
export async function mockUnblockIp(ip: string): Promise<{ unblocked: string }> {
  guard('解封 IP');
  if (!ip || ip.length === 0 || ip.length > 64) {
    throw new ApiError({ code: -1, message: 'invalid ip', httpStatus: 400 });
  }
  const index = MOCK_IP_BLACKLIST.findIndex((entry) => entry.ip === ip);
  if (index >= 0) MOCK_IP_BLACKLIST.splice(index, 1);
  return delay({ unblocked: ip });
}
